import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Bell,
  AlertTriangle,
  RefreshCw,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { alarmsService } from '@/services';
import type { ApiAlarm, AlarmFilters } from '@/types/api';
import { AlarmCard } from '@/components/alarms/AlarmCard';
import { AlarmFilters as AlarmFiltersComponent } from '@/components/alarms/AlarmFilters';
import { AlarmDetailDialog } from '@/components/alarms/AlarmDetailDialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { isScheduleVerificationAlarmType } from '@/utils/scheduleVerification';
import { cn } from '@/lib/utils';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Skeleton component
function AlarmCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="flex-1">
              <Skeleton className="h-5 w-64 mb-2" />
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-5 w-20" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-9 w-full" />
      </CardContent>
    </Card>
  );
}

export default function Alarms() {
  const { currentUser, isAuthenticated, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Estado de filtros
  const [filters, setFilters] = useState<AlarmFilters>({
    page: 1,
    pageSize: 20,
  });
  
  // Estado de diálogos
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAlarm, setSelectedAlarm] = useState<ApiAlarm | null>(null);
  const [acknowledgingId, setAcknowledgingId] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  // Query para obtener alarmas
  // company_admin: el backend filtra automáticamente por companyId
  // superadmin: ve todas las alarmas
  const shouldFetch = Boolean(isAuthenticated && !authLoading && currentUser);
  
  const { 
    data: alarmsData, 
    isLoading, 
    error,
    refetch,
    isFetching,
    status,
  } = useQuery({
    queryKey: ['alarms', filters, currentUser?.companyId],
    queryFn: async () => {
      const response = await alarmsService.getAll(filters);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar alarmas');
      }
      if (!response.data) {
        throw new Error('No se recibieron datos del servidor');
      }
      return response.data;
    },
    enabled: shouldFetch, // Solo ejecutar cuando hay usuario autenticado
    retry: 1, // Reintentar una vez en caso de error
  });
  
  // Debug: mostrar estado del query (temporal)
  if (process.env.NODE_ENV === 'development') {
    console.log('Alarms query debug:', {
      shouldFetch,
      isAuthenticated,
      authLoading,
      currentUser: currentUser?.id,
      status,
      isLoading,
      isFetching,
      hasData: !!alarmsData,
      error: error?.message,
    });
  }

  // Query para obtener estadísticas
  const { data: statsData } = useQuery({
    queryKey: ['alarms-stats', currentUser?.companyId],
    queryFn: async () => {
      const response = await alarmsService.getStats();
      if (!response.success) {
        return null;
      }
      return response.data;
    },
    enabled: Boolean(isAuthenticated && !authLoading && currentUser), // Solo ejecutar cuando hay usuario autenticado
  });

  // Mutation para reconocer alarma
  const acknowledgeMutation = useMutation({
    mutationFn: async (id: string) => {
      setAcknowledgingId(id);
      const response = await alarmsService.acknowledge(id);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al reconocer alarma');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alarms'] });
      queryClient.invalidateQueries({ queryKey: ['alarms-stats'] });
      toast({
        title: 'Alarma reconocida',
        description: 'La alarma ha sido marcada como reconocida.',
      });
      setAcknowledgingId(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      setAcknowledgingId(null);
    },
  });

  // Mutation para resolver alarma
  const resolveMutation = useMutation({
    mutationFn: async ({ id, actionTaken }: { id: string; actionTaken?: string }) => {
      setResolvingId(id);
      const response = await alarmsService.resolve(id, actionTaken ? { actionTaken } : undefined);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al resolver alarma');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alarms'] });
      queryClient.invalidateQueries({ queryKey: ['alarms-stats'] });
      toast({
        title: 'Alarma resuelta',
        description: 'La alarma ha sido marcada como resuelta.',
      });
      setResolvingId(null);
      setDetailDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      setResolvingId(null);
    },
  });

  const alarms = alarmsData?.items || [];
  const pagination = alarmsData?.pagination;
  const verificationAlarmsCount = alarms.filter((a) =>
    isScheduleVerificationAlarmType(a.type) && a.status === 'active'
  ).length;

  const handleViewDetails = (alarm: ApiAlarm) => {
    setSelectedAlarm(alarm);
    setDetailDialogOpen(true);
  };

  const handleAcknowledge = (id: string) => {
    acknowledgeMutation.mutate(id);
  };

  const handleResolve = (id: string, actionTaken?: string) => {
    resolveMutation.mutate({ id, actionTaken });
  };

  const handleFiltersChange = (newFilters: AlarmFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({ page: 1, pageSize: 20 });
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  const stats = statsData;

  // Mostrar skeleton mientras carga la autenticación
  if (authLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-9 w-48 mb-2" />
            <Skeleton className="h-5 w-64" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4">
          {[1, 2, 3].map((i) => (
            <AlarmCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground flex items-center gap-3">
            <Bell className="w-8 h-8 text-primary" />
            Alarmas
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestión y seguimiento de alarmas del sistema
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw className={cn('w-4 h-4 mr-2', isLoading && 'animate-spin')} />
          Actualizar
        </Button>
      </div>

      {verificationAlarmsCount > 0 && !filters.type && (
        <Alert className="border-amber-500/50 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
            <span>
              Hay {verificationAlarmsCount} alarma(s) activa(s) de verificación de programaciones en esta página.
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  type: 'schedule_mismatch',
                  status: 'active',
                  page: 1,
                }))
              }
            >
              Filtrar desajustes
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Estadísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Alarmas Activas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {stats.totalActive}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Críticas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {stats.totalAlarms}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Advertencias
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">
                {stats.totalWarnings}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Últimas 24h
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.last24Hours}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <AlarmFiltersComponent
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onReset={handleResetFilters}
      />

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : 'Error al cargar alarmas'}
          </AlertDescription>
        </Alert>
      )}

      {/* Lista de alarmas */}
      {isLoading ? (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 gap-4"
        >
          {[1, 2, 3, 4, 5].map((i) => (
            <AlarmCardSkeleton key={i} />
          ))}
        </motion.div>
      ) : alarms.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-lg font-medium">
              No se encontraron alarmas
            </p>
            <p className="text-muted-foreground text-sm mt-1">
              No hay alarmas que coincidan con los filtros seleccionados
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 gap-4"
          >
            {alarms.map((alarm) => (
              <motion.div key={alarm.id} variants={itemVariants}>
                <AlarmCard
                  alarm={alarm}
                  onViewDetails={handleViewDetails}
                  onAcknowledge={handleAcknowledge}
                  onResolve={handleResolve}
                />
              </motion.div>
            ))}
          </motion.div>

          {/* Paginación */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Mostrando {((pagination.page - 1) * pagination.pageSize) + 1} - {Math.min(pagination.page * pagination.pageSize, pagination.total)} de {pagination.total} alarmas
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Diálogo de detalles */}
      <AlarmDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        alarm={selectedAlarm}
        onAcknowledge={handleAcknowledge}
        onResolve={handleResolve}
        isAcknowledging={acknowledgingId === selectedAlarm?.id}
        isResolving={resolvingId === selectedAlarm?.id}
      />
    </div>
  );
}
